import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Layout() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);

  if (pathname.startsWith("/admin")) return <Outlet />;

  const link = ({ isActive }) => `nav-link ${isActive ? "active" : ""}`;
  const mobileLink = ({ isActive }) =>
    `block py-4 text-2xl font-semibold ${isActive ? "text-white" : "text-neutral-400"}`;
  const doLogout = () => { logout(); setOpen(false); nav("/"); };
  const closeAnd = (fn) => () => { setOpen(false); if (fn) fn(); };

  return (
    <div className="min-h-screen text-white" data-testid="public-layout">
      <header className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-6 pb-5 sm:pt-7 sm:pb-6 flex items-center justify-between gap-4">
        <NavLink to="/" className="text-xl sm:text-2xl font-bold tracking-tight" data-testid="logo-link">Life 101</NavLink>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          <NavLink to="/" end className={link} data-testid="nav-home">Ana Sayfa</NavLink>
          <NavLink to="/nasil-calisir" className={link} data-testid="nav-how">Nasıl Çalışır?</NavLink>
          <NavLink to="/test" className={link} data-testid="nav-test">Test</NavLink>
          {user && <NavLink to="/panelim" className={link} data-testid="nav-panel">Panelim</NavLink>}
          {user && <NavLink to="/kaynaklar" className={link} data-testid="nav-resources">Kaynaklar</NavLink>}
          {user && <NavLink to="/kitaplar" className={link} data-testid="nav-books">Kitaplar</NavLink>}
        </nav>

        {/* Desktop auth */}
        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <span className="text-sm text-neutral-400 hidden lg:inline" data-testid="user-greet">Merhaba, {user.first_name}</span>
              <button onClick={doLogout} className="btn-secondary text-sm inline-flex items-center gap-2" data-testid="user-logout">
                <LogOut size={14} /> Çıkış
              </button>
            </>
          ) : (
            <>
              <NavLink to="/giris" className="text-sm text-neutral-300 hover:text-white" data-testid="nav-login">Giriş</NavLink>
              <NavLink to="/kayit" className="btn-primary text-sm" data-testid="nav-register">Üye Ol</NavLink>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          className="md:hidden p-2 -mr-2 text-white"
          onClick={() => setOpen(true)}
          aria-label="Menüyü aç"
          data-testid="mobile-menu-open"
        >
          <Menu size={26} />
        </button>
      </header>
      <div className="glow-divider max-w-7xl mx-auto" />

      {/* Mobile drawer */}
      {open && (
        <div
          className="fixed inset-0 z-50 md:hidden bg-black/95 backdrop-blur-xl"
          onClick={() => setOpen(false)}
          data-testid="mobile-menu-drawer"
        >
          <div className="relative h-full flex flex-col p-6 overflow-y-auto pointer-events-none">
            <div className="flex items-center justify-between mb-10 pointer-events-auto">
              <span className="text-xl font-bold">Life 101</span>
              <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="p-2 -mr-2 text-white" aria-label="Menüyü kapat" data-testid="mobile-menu-close">
                <X size={26} />
              </button>
            </div>

            <nav className="space-y-1 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              <NavLink to="/" end className={mobileLink} onClick={closeAnd()} data-testid="m-nav-home">Ana Sayfa</NavLink>
              <NavLink to="/nasil-calisir" className={mobileLink} onClick={closeAnd()} data-testid="m-nav-how">Nasıl Çalışır?</NavLink>
              <NavLink to="/test" className={mobileLink} onClick={closeAnd()} data-testid="m-nav-test">Test</NavLink>
              {user && <NavLink to="/panelim" className={mobileLink} onClick={closeAnd()} data-testid="m-nav-panel">Panelim</NavLink>}
              {user && <NavLink to="/kaynaklar" className={mobileLink} onClick={closeAnd()} data-testid="m-nav-resources">Kaynaklar</NavLink>}
              {user && <NavLink to="/kitaplar" className={mobileLink} onClick={closeAnd()} data-testid="m-nav-books">Kitaplar</NavLink>}
            </nav>

            <div className="mt-auto pt-8 border-t border-white/10 pointer-events-auto" onClick={(e) => e.stopPropagation()}>
              {user ? (
                <>
                  <div className="text-sm text-neutral-400 mb-4">Merhaba, <span className="text-white">{user.first_name}</span></div>
                  <button onClick={doLogout} className="btn-secondary w-full inline-flex items-center justify-center gap-2" data-testid="m-user-logout">
                    <LogOut size={16} /> Çıkış Yap
                  </button>
                </>
              ) : (
                <div className="space-y-3">
                  <NavLink to="/kayit" onClick={closeAnd()} className="btn-primary w-full inline-flex items-center justify-center" data-testid="m-nav-register">Üye Ol</NavLink>
                  <NavLink to="/giris" onClick={closeAnd()} className="btn-secondary w-full inline-flex items-center justify-center" data-testid="m-nav-login">Giriş Yap</NavLink>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 lg:py-16">
        <Outlet />
      </main>
      <footer className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-8 sm:py-10 mt-6 sm:mt-10 text-xs text-neutral-500 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
        <span>Verilerin 256-bit SSL ile korunur ve asla paylaşılmaz.</span>
        <span>Life 101 © {new Date().getFullYear()} Tüm hakları saklıdır.</span>
      </footer>
    </div>
  );
}
